/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      // Vor updatePrice: wirft dort etwas, blieben die Bloecke sonst mit den Werten der
      // vorher gewaehlten Variante stehen - genau der Fall, den sie verbergen sollen.
      this.hideRefetchedPriceBlocks();
      this.updatePrice();
      this.updateSectionsWithFetching();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        // Canopy 7.3 changed QuickAddDrawer.updateMedia() to take the whole media
        // object instead of just its id. Passing the id made it throw on
        // `media.preview_image`, which aborted the rest of handleVariantChange -
        // so the form's variant id was never updated and the drawer added the
        // default variant instead of the selected one.
        this.section.updateMedia(this.variant.featured_media);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        // Canopy 7.3 removed assets/pickup-availability.js, so <pickup-availability> is
        // an inert element. It is still rendered into the DOM, so the null check above
        // passes and calling the method would throw a TypeError - which would abort the
        // rest of the variant change (SKU update and the on:variant:change event).
        if (typeof this.pickUpAvailability.getAvailability === 'function') {
          this.pickUpAvailability.getAvailability(this.variant.id);
        }
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');
        const priceCurrentEl_b = this.price.querySelector('.price__current_b');
        const priceWasEl_b = this.price.querySelector('.price_compare_was');
        const priceSaving_b = this.price.querySelector('.product-sale-badge-b');

        // Update current price and original price if on sale.
        //
        // CK: Die Zuweisung an priceWasEl_b stand ohne Klammern unter dem if und lief
        // deshalb immer - auch wenn es das Element gar nicht gibt. Bei einem Produkt ohne
        // Vergleichspreis rendert das Theme .price_compare_was nicht, der Variantenwechsel
        // brach dort mit einer TypeError ab, und alles danach in handleVariantChange blieb
        // liegen: das Nachladen der Preisbloecke, Gewicht, Barcode, SKU, Abholung.
        if (priceCurrentEl) {
          priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        }
        if (priceCurrentEl_b) {
          priceCurrentEl_b.innerHTML = this.data.formatted_b[this.variant.id].price;
        }
        if (priceWasEl) {
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';
        }
        if (priceWasEl_b) {
          priceWasEl_b.innerHTML = this.data.formatted_b[this.variant.id].compareAtPrice || '';
        }

        if (priceWasEl && priceSaving_b && this.variant.compare_at_price > this.variant.price) {
          const diff = this.variant.compare_at_price - this.variant.price;
          const percent = (diff * 100) / this.variant.compare_at_price;
          // CK: Runden statt abschneiden, sonst wird z. B. 9,99 % als 9 % ausgegeben.
          priceSaving_b.innerHTML = `-${Math.round(percent)}%`;
        }

        // Update unit price, if specified.
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const bestPriceEl = this.price.querySelector('.price-30-day-best span');

          var reference_value = 0;
          var unitPrice = 0;
          var best_price_30_day = 0;
          // forloop through the array to get the value and unit of the variant
          for (let i = 0; i < variantData.length; i++) {
            // check if the key matches the variant id
            if (variantData[i].id == this.variant.id) {
              // get the value and unit of the variant
              reference_value = variantData[i].unit;
              unitPrice = variantData[i].price;
              best_price_30_day = variantData[i].best_price_30_day;
            }
          }


          // change unitPrice 3.0 to 3.00
          unitPrice = parseFloat(unitPrice);
          unitPrice = unitPrice.toFixed(2);
          // change 3.15 to "€3 <sup>15</sup>"
          unitPrice = unitPrice.toString().replace('.', '<sup>');
          unitPrice = unitPrice + '</sup>';
          unitPrice = '€' + unitPrice;


          if (valueEl) {
            valueEl.innerHTML = unitPrice;
          }
          if (unitEl) {
            unitEl.textContent = `${reference_value}`;
          }
          if(bestPriceEl){
            bestPriceEl.textContent = best_price_30_day;
          }

        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      const defaultEl = this.price.querySelector('.price__default');
      const noVariantEl = this.price.querySelector('.price__no-variant');
      if (defaultEl) defaultEl.hidden = !this.variant;
      if (noVariantEl) noVariantEl.hidden = this.variant;
    }

    /**
     * Verbirgt die Bloecke, die erst updateSectionsWithFetching wieder fuellt.
     *
     * Der Preis oben wechselt sofort aus den eingebetteten Variantendaten, der Aktionspreis
     * und der 30-Tage-Bestpreis kommen dagegen aus dem neu geladenen Markup - das dauert
     * rund eine Sekunde. So lange stand unter dem neuen Preis der Aktionspreis der vorher
     * gewaehlten Variante: bei 4x 5 L "€52,95" und darunter "Im Warenkorb mit 15 % Rabatt:
     * €17,81", was der Rabatt auf 1x 5 L ist.
     *
     * Eine kurze Luecke ist besser als eine falsche Preisangabe - beides sind Angaben nach
     * § 11 PAngV. Faellt das Nachladen ganz aus, bleibt die Stelle leer statt falsch.
     *
     * visibility statt display oder leerem Markup: der Platz bleibt reserviert, sonst
     * springt der Inhalt darunter fuer die Dauer des Nachladens nach oben und wieder zurueck.
     */
    hideRefetchedPriceBlocks() {
      document.querySelectorAll('.price-30-day-best-wrapper').forEach((wrapper) => {
        wrapper.style.visibility = 'hidden';
      });
    }

    /**
     * Updates Sections with Fetching.
     */
    updateSectionsWithFetching() {
      const newUrl = new URL(window.location.href);
      const id = this.variant ? this.variant.id : null;
      if (id) {
        newUrl.searchParams.set("variant", id);
      } else {
        newUrl.searchParams.delete("variant");
      }
      fetch(newUrl.toString())
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if(html.querySelector('.opt-savings-container')){
          const all_opt_savings_container = html.querySelectorAll('.opt-savings-container');
          document.querySelectorAll('.opt-savings-container').forEach((opt_savings_container, index) => {
            opt_savings_container.innerHTML = all_opt_savings_container[index].innerHTML;
          });
        }

        if(html.querySelector('.price-30-day-best-wrapper')){
          const all_price_30_day_best_wrapper = html.querySelectorAll('.price-30-day-best-wrapper');
          document.querySelectorAll('.price-30-day-best-wrapper').forEach((price_30_day_best_wrapper, index) => {
            if(all_price_30_day_best_wrapper[index]){
              price_30_day_best_wrapper.innerHTML = all_price_30_day_best_wrapper[index].innerHTML;
              price_30_day_best_wrapper.style.visibility = '';
            }
          });
        }

        if(html.querySelector('.product-detail-properties')){
          const all_product_detail_properties = html.querySelectorAll('.product-detail-properties');
          document.querySelectorAll('.product-detail-properties').forEach((product_detail_properties, index) => {
            product_detail_properties.innerHTML = all_product_detail_properties[index].innerHTML;
          });
        }

        if(html.querySelector('.hazard-info-container')){
          const all_hazard_info_container = html.querySelectorAll('.hazard-info-container');
          document.querySelectorAll('.hazard-info-container').forEach((hazard_info_container, index) => {
            hazard_info_container.innerHTML = all_hazard_info_container[index].innerHTML;
          });
        }

        const openButton = document.getElementById('open_subscriptions');
        const subscriptionsFieldset = document.querySelector('section:not(.shopify_subscriptions_app_block--hidden) .shopify_subscriptions_fieldset');

        if(openButton){
          if (subscriptionsFieldset) {
            openButton.style.display = 'flex';
          }

          function toggleSubscriptions() {
              if (!subscriptionsFieldset.classList.contains('show')) {
                  subscriptionsFieldset.style.height = subscriptionsFieldset.scrollHeight + 'px';
                  subscriptionsFieldset.classList.add('show');
                  openButton.classList.add('transform');
              } else {
                  subscriptionsFieldset.style.height = '0';
                  subscriptionsFieldset.classList.remove('show');
                  openButton.classList.remove('transform');
              }
          }
          openButton.addEventListener('click', toggleSubscriptions);

          openButton.addEventListener('keydown', function(event) {
              if (event.key === 'Enter') {
                  toggleSubscriptions();
              }
          });
        }

        if(subscriptionsFieldset){
          subscriptionsFieldset.addEventListener('transitionend', function() {
            if (subscriptionsFieldset.classList.contains('show')) {
                subscriptionsFieldset.style.height = 'auto';
            }
        });
        }

        if(html.querySelector('shopify-accelerated-checkout')){
          // check if the accelerated checkout is disabled
          const accelerated_checkout = html.querySelector('shopify-accelerated-checkout');
          if(accelerated_checkout.hasAttribute('disabled')){
            document.querySelector('.shopify-payment-button').style.display = 'none';
          } else {
            document.querySelector('.shopify-payment-button').style.display = 'block';
          }
        }

      })
      .catch((error) => {
        // Ohne diesen Zweig blieb ein fehlgeschlagenes Nachladen unbemerkt. Die verborgenen
        // Preisbloecke bleiben dann verborgen - keine Angabe ist besser als die der vorher
        // gewaehlten Variante.
        console.warn('Variantenwechsel: Nachladen der Seite fehlgeschlagen', error);
      });
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

/**
 * Updates the url with the selected variant id without stripping other parameters.
 * @param {object} evt - Event object.
 */
updateUrl(evt) {
  if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;

  // 1. Aktuelle URL-Parameter laden
  const urlParams = new URLSearchParams(window.location.search);

  // 2. Nur die Variant-ID setzen oder aktualisieren
  urlParams.set('variant', this.variant.id);

  // 3. Die neue URL zusammenbauen (Pfad + aktualisierte Parameter)
  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;

  // 4. URL in der Adressleiste aktualisieren
  window.history.replaceState({}, '', newUrl);
}

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
